import React from 'react';
import PropTypes from 'prop-types';
import {
	ALL_HEALTH_VALUES,
	HEALTH_DESCRIPTION,
	HEALTH_LABEL,
	HEALTH_TONE,
} from './HealthChip';
import { formatNumber } from './datasetMonitorFormat';
import HelpTip from './HelpTip';

const DEFAULT_TONE = 'grey';

/**
 * DatasetHealthSummary
 *
 * An at-a-glance count of datasets per health verdict, shown above the table.
 *
 * The table alone cannot answer "is anything wrong right now?" without
 * reading sixty rows, and the answer is the reason the page exists. This
 * strip collapses that to one line, ordered by the same first-match-wins
 * severity sequence the backend evaluates in (design spec 9), so the states
 * that matter appear leftmost.
 *
 * Verdicts with a zero count are omitted rather than rendered as empty
 * chips: a page whose whole premise is that a red row means something
 * should not display six mostly-blank counters competing with the two that
 * are real.
 *
 * Clicking a tile filters the table to that verdict, so the summary is a
 * control rather than decoration.
 */
const DatasetHealthSummary = ({
	datasets,
	selectedHealth,
	onSelectHealth,
	onShowLegend,
}) => {
	if (!datasets || datasets.length === 0) {
		return null;
	}

	const counts = datasets.reduce((acc, dataset) => {
		const key = dataset.health;
		acc[key] = (acc[key] || 0) + 1;
		return acc;
	}, {});

	const present = ALL_HEALTH_VALUES.filter((health) => counts[health] > 0);

	if (present.length === 0) {
		return null;
	}

	return (
		<div className="dataset-health-summary" role="group" aria-label="Dataset health summary">
			{present.map((health) => {
				const tone = HEALTH_TONE[health] || DEFAULT_TONE;
				const isActive = selectedHealth === health;
				const label = HEALTH_LABEL[health] || health;
				const description = HEALTH_DESCRIPTION[health];

				return (
					<div className="dataset-health-tile-wrap" key={health}>
						<button
							type="button"
							className={`dataset-health-tile tile-${tone}${isActive ? ' is-active' : ''}`}
							onClick={() => onSelectHealth(isActive ? '' : health)}
							aria-pressed={isActive}
						>
							<span className="dataset-health-tile-count">
								{formatNumber(counts[health])}
							</span>
							<span className="dataset-health-tile-label">{label}</span>
						</button>
						{description && <HelpTip label={label} text={description} />}
					</div>
				);
			})}

			{/* Sits at the end of the strip, in space the tiles never fill. The
			    tiles omit any verdict with a zero count, so this is the only
			    place every status is explained regardless of today's numbers. */}
			<button
				type="button"
				className="dataset-health-legend-button"
				onClick={onShowLegend}
			>
				<i className="bi bi-question-circle me-1" aria-hidden="true" />
				What do these mean?
			</button>
		</div>
	);
};

DatasetHealthSummary.propTypes = {
	datasets: PropTypes.arrayOf(
		PropTypes.shape({
			health: PropTypes.string,
		}),
	),
	selectedHealth: PropTypes.string,
	onSelectHealth: PropTypes.func.isRequired,
	onShowLegend: PropTypes.func.isRequired,
};

export default DatasetHealthSummary;
